import React, { ReactNode, useMemo } from 'react';
import { Box } from '@chakra-ui/react';

interface TwoRowGridProps {
    firstRowItems: ReactNode[];
    secondRowItems: ReactNode[];
    secondRowWidths?: string[];  // Width untuk setiap box di baris kedua
    gap?: number | string;
    firstRowHeight?: string;    // Tinggi untuk baris pertama
    secondRowHeight?: string;   // Tinggi untuk baris kedua
    style?: React.CSSProperties;
    [key: string]: any;
}

/**
 * TwoRowGrid - Komponen grid khusus dengan 2 baris yang responsive
 * Baris pertama: Beberapa box dengan lebar otomatis
 * Baris kedua: 3 box dengan lebar custom
 */
const TwoRowGrid: React.FC<TwoRowGridProps> = ({
    firstRowItems,
    secondRowItems,
    secondRowWidths = ['1fr', '2fr', '1fr'], // Default widths untuk baris kedua
    gap = 6,
    firstRowHeight = '100%',   // Tinggi tetap untuk baris pertama
    secondRowHeight = '100%',  // Tinggi tetap untuk baris kedua
    style,
    ...rest
}) => {
    // Buat template columns untuk baris kedua
    const secondRowTemplateColumns = secondRowWidths.join(' ');

    // Responsive grid columns untuk baris pertama
    const firstRowColumns = Math.min(4, firstRowItems.length);

    // Generate stable keys
    const firstRowKeys = useMemo(() => 
        firstRowItems.map((_, index) => `first-row-${Date.now()}-${index}`), 
        [firstRowItems.length]
    );
    
    const secondRowKeys = useMemo(() => 
        secondRowItems.map((_: ReactNode, index: number) => `second-row-${Date.now()}-${index}`), 
        [secondRowItems.length]
    );

    return (
        <Box 
            h="100%"
            display="flex" 
            flexDirection="column" 
            gap={gap}
            overflow="auto"
            {...rest}
        >
            {/* Baris Pertama - Box dengan layout responsive */}
            <Box
                display="grid"
                gridTemplateColumns={`repeat(${firstRowColumns}, 1fr)`}
                gap={gap}
                h={firstRowHeight}
                minH="280px"
                overflow="hidden"
            >
                {firstRowItems.map((item, index) => (
                    <Box
                        key={firstRowKeys[index]}
                        w="100%"
                        h="100%"
                        overflow="hidden"
                        bg="white"
                        rounded="lg"
                        shadow="md"
                        display="flex"
                        flexDirection="column"
                        gridColumn={
                            // Jika ini adalah box terakhir dan tidak cukup untuk mengisi satu baris penuh
                            (index === firstRowItems.length - 1 &&
                                firstRowItems.length % 4 !== 0 &&
                                firstRowItems.length > 1)
                                ? `span ${Math.min(4, 4 - ((firstRowItems.length - 1) % 4))}`
                                : undefined
                        }
                    >
                        {item}
                    </Box>
                ))}
            </Box>

            {/* Baris Kedua - Box dengan lebar custom */}
            <Box
                display="grid"
                gridTemplateColumns={secondRowTemplateColumns}
                gap={gap}
                h={secondRowHeight}
                overflow="hidden"
            >
                {secondRowItems.map((item: ReactNode, index: number) => (
                    <Box 
                        key={secondRowKeys[index]} 
                        w="100%" 
                        h="100%" 
                        overflow="hidden"
                        bg="white"
                        rounded="lg"
                        shadow="md"
                        display="flex"
                        flexDirection="column"
                    >
                        {item}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default TwoRowGrid;